import { Suspense, lazy } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";

const Accent3D = lazy(() => import("../components/Accent3D"));

export default function About() {
  return (
    <PageTransition>
      <FXBackdrop>
        {/* HERO — light */}
        <section className="containerX pt-10 md:pt-16 pb-10 relative">
          <div className="float-shape bg-violet-400 h-14 w-14 top-2 right-16 hidden lg:block" />
          <div className="float-shape-slow bg-purple-300 h-8 w-8 bottom-6 left-4 hidden lg:block" style={{ animationDelay: "1.5s" }} />

          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <div className="chip">
                <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} /> About
              </div>
              <h1 className="h1 mt-5">We're building a <span className="text-black">high-signal hiring layer.</span></h1>
              <p className="p mt-4 max-w-3xl">
                Traditional recruiting is noisy: too many resumes, too little context.
                Our system focuses on verified screening, structured scorecards, and faster loops.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:flex items-center justify-center"
            >
              <Suspense fallback={<div className="h-[280px]" />}>
                <Accent3D variant="about" className="w-full h-[280px]" />
              </Suspense>
            </motion.div>
          </div>
        </section>

        {/* VALUES — dark */}
        <section className="dark-section py-12 md:py-16 relative overflow-hidden">
          <div className="float-shape bg-violet-500 h-20 w-20 -top-6 -right-6" style={{ animationDelay: "0.5s" }} />

          <div className="containerX">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                ["Mission", "Make hiring transparent, fast, and fair for both sides."],
                ["Principles", "Signal over volume. Notes over guesswork. Feedback always."],
                ["Quality Bar", "We'd rather send 4 strong candidates than 40 random ones."],
              ].map(([t, d], i) => (
                <motion.div
                  key={t} className="glass-dark p-6"
                  whileHover={{ y: -7, scale: 1.04, boxShadow: "0 20px 50px rgba(139,92,246,0.18)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="font-semibold">{t}</div>
                  <div className="p mt-2">{d}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="mt-10 glass-dark p-8"
              whileHover={{ scale: 1.015, boxShadow: "0 28px 70px rgba(139,92,246,0.2)" }}
              transition={{ duration: 0.35 }}
            >
              <div className="text-xl font-semibold">What makes us different </div>
              <ul className="mt-4 grid gap-3 text-white/80">
                {[
                  "Scorecards on every candidate",
                  "Clear timelines and weekly updates",
                  "Screening notes: strengths + risks",
                  "Support through offer close",
                ].map((x, i) => (
                  <motion.li key={x}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false, amount: 0.1 }}
                    transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                    className="flex items-center gap-3"
                  >
                    <motion.span
                      className="h-2 w-2 rounded-full bg-violet-400 inline-block flex-shrink-0"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                    />
                    {x}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>
      </FXBackdrop>
    </PageTransition>
  );
}
